import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ShoppingCart,
  Filter,
  Plus,
  UtensilsCrossed,
  Coffee,
  IceCream,
  Beer,
  Sandwich,
  Clock,
} from 'lucide-react';
import { useCartStore } from '../../store/cart';
import { formatPrice } from '../../lib/utils';

// Placeholder images
const FOOD_IMAGES = {
  burger: 'https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=400&q=80',
  hotdog: 'https://images.unsplash.com/photo-1612392062422-ef19b42f74df?w=400&q=80',
  fries: 'https://images.unsplash.com/photo-1573080496219-bb080dd4f877?w=400&q=80',
  pizza: 'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=400&q=80',
  nachos: 'https://images.unsplash.com/photo-1513456852971-30c0b8199d4d?w=400&q=80',
  coffee: 'https://images.unsplash.com/photo-1509042239860-f550ce710b93?w=400&q=80',
  softdrink: 'https://images.unsplash.com/photo-1581006852262-e4307cf6283a?w=400&q=80',
  beer: 'https://images.unsplash.com/photo-1535958636474-b021ee887b13?w=400&q=80',
  icecream: 'https://images.unsplash.com/photo-1501443762994-82bd5dace89a?w=400&q=80',
  popcorn: 'https://images.unsplash.com/photo-1585647347483-22b66260dfff?w=400&q=80',
};

const menuItems = [
  {
    id: 'food-1',
    name: 'Racing Burger',
    description: 'Beef patty with bacon, cheese, lettuce & special sauce',
    price: 1600,
    category: 'Mains',
    image: FOOD_IMAGES.burger,
    prepTime: '10-15 min',
    popular: true,
  },
  {
    id: 'food-2',
    name: 'Pit Lane Hot Dog',
    description: 'Classic hot dog with mustard, ketchup & onions',
    price: 1000,
    category: 'Mains',
    image: FOOD_IMAGES.hotdog,
    prepTime: '5-8 min',
    popular: true,
  },
  {
    id: 'food-3',
    name: 'Loaded Fries',
    description: 'Crispy fries with cheese sauce, bacon & jalapeños',
    price: 1200,
    category: 'Sides',
    image: FOOD_IMAGES.fries,
    prepTime: '8-10 min',
    popular: false,
  },
  {
    id: 'food-4',
    name: 'Pepperoni Pizza Slice',
    description: 'Classic pepperoni with mozzarella',
    price: 800,
    category: 'Mains',
    image: FOOD_IMAGES.pizza,
    prepTime: '5-7 min',
    popular: true,
  },
  {
    id: 'food-5',
    name: 'Nachos Supreme',
    description: 'Corn chips with beef, cheese, salsa & sour cream',
    price: 1400,
    category: 'Mains',
    image: FOOD_IMAGES.nachos,
    prepTime: '8-12 min',
    popular: false,
  },
  {
    id: 'food-6',
    name: 'Popcorn Bucket',
    description: 'Fresh buttered popcorn',
    price: 700,
    category: 'Snacks',
    image: FOOD_IMAGES.popcorn,
    prepTime: '2-3 min',
    popular: false,
  },
  {
    id: 'drink-1',
    name: 'Barista Coffee',
    description: 'Latte, cappuccino, flat white or long black',
    price: 550,
    category: 'Drinks',
    image: FOOD_IMAGES.coffee,
    prepTime: '3-5 min',
    popular: true,
  },
  {
    id: 'drink-2',
    name: 'Soft Drink',
    description: 'Coke, Sprite, Fanta or Solo',
    price: 450,
    category: 'Drinks',
    image: FOOD_IMAGES.softdrink,
    prepTime: '1 min',
    popular: false,
  },
  {
    id: 'drink-3',
    name: 'Cold Beer',
    description: 'XXXX Gold, Great Northern or local craft',
    price: 900,
    category: 'Drinks',
    image: FOOD_IMAGES.beer,
    prepTime: '1 min',
    popular: true,
  },
  {
    id: 'food-7',
    name: 'Sundae',
    description: 'Vanilla ice cream with chocolate sauce & sprinkles',
    price: 650,
    category: 'Desserts',
    image: FOOD_IMAGES.icecream,
    prepTime: '2-3 min',
    popular: false,
  },
];

const categories = [
  { id: 'All', icon: UtensilsCrossed, label: 'All' },
  { id: 'Mains', icon: Sandwich, label: 'Mains' },
  { id: 'Sides', icon: UtensilsCrossed, label: 'Sides' },
  { id: 'Snacks', icon: IceCream, label: 'Snacks' },
  { id: 'Drinks', icon: Coffee, label: 'Drinks' },
  { id: 'Desserts', icon: IceCream, label: 'Desserts' },
];

export function FoodPage() {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const { addItem, totalItems } = useCartStore();

  const filteredItems =
    selectedCategory === 'All'
      ? menuItems
      : menuItems.filter((item) => item.category === selectedCategory);

  const popularItems = menuItems.filter((item) => item.popular);

  const handleAddToCart = (item: (typeof menuItems)[0]) => {
    addItem({
      productId: item.id,
      name: item.name,
      price: item.price,
      quantity: 1,
      type: 'food',
      imageUrl: item.image,
    });
  };

  return (
    <div className="py-12">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Track Food & Drinks</h1>
            <p className="text-gray-600">
              Order ahead and skip the queue! Pick up at any concession stand.
            </p>
          </div>

          <Link
            to="/cart"
            className="relative flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors"
          >
            <ShoppingCart size={20} />
            <span className="font-medium">Cart</span>
            {totalItems() > 0 && (
              <span className="absolute -top-2 -right-2 w-6 h-6 bg-white text-red-600 rounded-full flex items-center justify-center text-sm font-bold">
                {totalItems()}
              </span>
            )}
          </Link>
        </div>

        {/* Popular Section */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Popular Items</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {popularItems.slice(0, 4).map((item) => (
              <div
                key={item.id}
                className="card overflow-hidden group cursor-pointer"
                onClick={() => handleAddToCart(item)}
              >
                <div className="relative h-32">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute top-2 right-2 px-2 py-1 bg-red-600 text-white text-xs font-medium rounded-full">
                    Popular
                  </div>
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-lg">
                        <Plus size={20} className="text-red-600" />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="p-3">
                  <h3 className="font-semibold text-gray-900">{item.name}</h3>
                  <div className="flex items-center justify-between mt-1">
                    <span className="font-bold text-red-600">{formatPrice(item.price)}</span>
                    <span className="text-xs text-gray-500 flex items-center">
                      <Clock size={12} className="mr-1" />
                      {item.prepTime}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Category Filter */}
        <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
          <Filter size={18} className="text-gray-400 flex-shrink-0" />
          {categories.map((cat) => {
            const Icon = cat.icon;
            return (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  selectedCategory === cat.id
                    ? 'bg-red-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Icon size={16} />
                {cat.label}
              </button>
            );
          })}
        </div>

        {/* Menu Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredItems.map((item) => (
            <div key={item.id} className="card overflow-hidden">
              <div className="relative h-48">
                <img
                  src={item.image}
                  alt={item.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-3 left-3">
                  <span className="px-2 py-1 bg-white/90 backdrop-blur text-xs font-medium rounded-full">
                    {item.category}
                  </span>
                </div>
              </div>

              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-semibold text-gray-900">{item.name}</h3>
                  <span className="text-lg font-bold text-gray-900">
                    {formatPrice(item.price)}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-3">{item.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500 flex items-center">
                    <Clock size={12} className="mr-1" />
                    {item.prepTime}
                  </span>
                  <button
                    onClick={() => handleAddToCart(item)}
                    className="flex items-center gap-1 px-3 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <Plus size={16} />
                    Add to Cart
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Pickup Info */}
        <div className="mt-12 bg-gray-900 rounded-2xl p-8 text-white">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-14 h-14 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <ShoppingCart size={24} />
              </div>
              <h3 className="font-semibold mb-2">Order Online</h3>
              <p className="text-sm text-gray-400">
                Browse the menu and add items to your cart
              </p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock size={24} />
              </div>
              <h3 className="font-semibold mb-2">Skip the Queue</h3>
              <p className="text-sm text-gray-400">
                Your order is prepared while you enjoy the racing
              </p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <UtensilsCrossed size={24} />
              </div>
              <h3 className="font-semibold mb-2">Collect & Enjoy</h3>
              <p className="text-sm text-gray-400">
                Pick up from any concession stand when ready
              </p>
            </div>
          </div>
        </div>

        {/* Location Note */}
        <div className="mt-8 p-4 bg-orange-50 border border-orange-200 rounded-xl">
          <div className="flex items-start gap-3">
            <Beer className="text-orange-600 flex-shrink-0 mt-0.5" size={20} />
            <div>
              <p className="font-medium text-orange-900">Alcohol Service</p>
              <p className="text-sm text-orange-700">
                Valid ID required for alcohol purchases. Alcohol is only served at licensed
                concession stands. Please drink responsibly.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
